//
// ChatPhoto - Component for displaying photo messages in chat
// Shows images shared by users with author information, timestamps, and controls
//

import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
  memo,
  useState,
} from 'react';

import {
  Column,
  Row,
} from '../../../display';

import ChatMessageControls from './ChatMessageControls';
import {
  ChatAuthorIcon,
  ChatAuthorName,
  ChatDate,
  ChatItemClick,
  ChatPic,
  MessageFrame,
} from './components';

const ChatPhoto = memo(({
  registerChild,
  translate,
  style,
  message,
  author,
  initials,
  crBlocked,
  color,
  gcatCode,
  getMedia,
  csid,
  visibleInThisGathering = true,
  isScrolling,
  isVisible,
  grpGpid,
  grpLbl,
  grpNbr,
  groupsMap,
  imagesMap,
  refreshCellSize,
  prtnrLogoUrl,
  selectGathering,
}) => {
  const [showControls, setShowControls] = useState(false);
  const {
    id,
    gthrId,
    msgRdatim1,
  } = message;

  return (
    <div
      ref={registerChild}
      key={id}
      className={classNames(
        'w-full py-2 gap-1 flex flex-col relative items-center justify-center overflow-hidden group',
        {
          'px-6': visibleInThisGathering,
          'px-2 opacity-50': !visibleInThisGathering,
        },
      )}
      style={style}
      // https://stackoverflow.com/a/61613591/9316077
      onMouseEnter={() => { setShowControls(true); }}
      onMouseLeave={() => { setShowControls(false); }}
    >
      <ChatItemClick
        gthrId={gthrId}
        selectGathering={selectGathering}
        visibleInThisGathering={visibleInThisGathering}
      />

      <ChatAuthorName
        translate={translate}
        author={author}
        initials={initials}
        crBlocked={crBlocked}
        gcatCode={gcatCode}
        grpNbr={grpNbr}
        prtnrLogoUrl={prtnrLogoUrl}
      />

      <Row
        start={!!author}
        end={!author}
        fullw
      >
        <ChatAuthorIcon
          author={author}
          initials={initials}
          color={color}
          gcatCode={gcatCode}
          grpGpid={grpGpid}
          grpLbl={grpLbl}
          groupsMap={groupsMap}
          imagesMap={imagesMap}
        />

        <MessageFrame
          key={id}
          author={author}
          visibleInThisGathering={visibleInThisGathering}
        >
          <ChatPic
            translate={translate}
            message={message}
            getMedia={getMedia}
            csid={csid}
            refreshCellSize={refreshCellSize}
          />
        </MessageFrame>

        <Column
          className={classNames(
            'self-start',
            {
              'order-2': !author,
              'order-4': author,
            },
          )}
          gap={1}
          crossStart={author}
          crossEnd={!author}
        >
          <ChatDate
            dtime={msgRdatim1}
            myMessage={!author}
          />

          <ChatMessageControls
            translate={translate}
            message={message}
            author={author}
            myMessage={!author}
            csid={csid}
            showControls={showControls}
            isScrolling={isScrolling}
            isVisible={isVisible}
          />
        </Column>

      </Row>
    </div>
  );
});
ChatPhoto.displayName = 'ChatPhoto';

ChatPhoto.propTypes = {
  registerChild: PropTypes.func.isRequired,
  translate: PropTypes.func.isRequired,
  style: PropTypes.shape({}).isRequired,
  author: PropTypes.string,
  initials: PropTypes.string,
  crBlocked: PropTypes.number,
  color: PropTypes.string,
  gcatCode: PropTypes.string,
  getMedia: PropTypes.func.isRequired,
  csid: PropTypes.string.isRequired,
  visibleInThisGathering: PropTypes.bool,
  isScrolling: PropTypes.bool.isRequired,
  isVisible: PropTypes.bool.isRequired,
  grpGpid: PropTypes.string,
  grpLbl: PropTypes.string,
  grpNbr: PropTypes.number,
  groupsMap: PropTypes.instanceOf(Map).isRequired,
  imagesMap: PropTypes.instanceOf(Map).isRequired,
  selectGathering: PropTypes.func,
  message: PropTypes.shape({
    id: PropTypes.number.isRequired,
    gthrId: PropTypes.number,
    // msgId: PropTypes.number.isRequired,
    msgTxt: PropTypes.string,
    msgRdatim1: PropTypes.string.isRequired,
    qstnId: PropTypes.string,
  }).isRequired,
  refreshCellSize: PropTypes.func.isRequired,
  prtnrLogoUrl: PropTypes.string,
};

export default ChatPhoto;
